import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useCurrentUser } from "../context/AuthContext";
import { nameFor } from "../utils/displayName";

const GET_THREAD = gql`
  query ThreadDetail($threadId: Int!) {
    chatThreadById(id: $threadId) {
      id bookingId guestId hostId
      bookingByBookingId {
        startAt endAt status
        saunaBySaunaId { id name }
      }
      userByGuestId { userProfileByUserId { fullName displayName } }
      userByHostId  { userProfileByUserId { fullName displayName } }
    }
  }
`;

const THREAD_MESSAGES = gql`
  query ThreadMessages($threadId: Int!) {
    threadMessages(threadId: $threadId) {
      nodes {
        id body senderId createdAt
      }
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($threadId: Int!, $body: String!) {
    sendMessage(input: { threadId: $threadId, body: $body }) {
      chatMessage { id body senderId createdAt }
    }
  }
`;

const MARK_READ = gql`
  mutation MarkThreadRead($threadId: Int!) {
    markThreadRead(input: { threadId: $threadId }) {
      chatThread { id guestLastReadAt hostLastReadAt }
    }
  }
`;

function displayName(user: { userProfileByUserId?: { fullName?: string; displayName?: string | null } | null }) {
  return nameFor(user.userProfileByUserId, "User");
}

function fmtTime(s: string) {
  return new Date(s).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function MessageThread() {
  const { threadId } = useParams<{ threadId: string }>();
  const tid = parseInt(threadId!, 10);
  const currentUserId = useCurrentUser();

  const [body, setBody] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: threadData, loading: threadLoading } = useQuery(GET_THREAD, {
    variables: { threadId: tid },
    fetchPolicy: "network-only",
  });

  const { data: msgData, loading: msgLoading, refetch } = useQuery(THREAD_MESSAGES, {
    variables: { threadId: tid },
    fetchPolicy: "network-only",
  });

  const [markRead] = useMutation(MARK_READ, {
    refetchQueries: ["MyUnreadCount", "MyThreads"],
  });

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE, {
    refetchQueries: ["ThreadMessages", "MyThreads", "MyUnreadCount"],
  });

  useEffect(() => {
    markRead({ variables: { threadId: tid } });
  }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgData]);

  if (threadLoading || msgLoading) return <p style={{ padding: 24 }}>Loading...</p>;

  const thread = threadData?.chatThreadById;
  if (!thread) return <p style={{ padding: 24, color: "#ef4444" }}>Thread not found.</p>;

  const isGuest    = currentUserId === thread.guestId;
  const counterpart = isGuest ? thread.userByHostId : thread.userByGuestId;
  const sauna      = thread.bookingByBookingId?.saunaBySaunaId;
  const messages   = msgData?.threadMessages?.nodes ?? [];

  async function handleSend() {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await sendMessage({ variables: { threadId: tid, body: trimmed } });
      setBody("");
    } catch (err: any) {
      alert(err?.message ?? "Failed to send");
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div style={{ maxWidth: 640, margin: "40px auto", padding: "0 24px 60px" }}>
      <Link to="/messages" style={{ fontSize: 13, color: "#2563eb", textDecoration: "none" }}>
        ← Back to messages
      </Link>

      {/* Header */}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <h2 style={{ margin: "0 0 4px" }}>
          {counterpart ? displayName(counterpart) : "Thread"}
        </h2>
        {sauna && (
          <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
            Re: <Link to={`/saunas/${sauna.id}`} style={{ color: "#2563eb" }}>{sauna.name}</Link>
            {" · "}
            <Link to={`/bookings/${thread.bookingId}`} style={{ color: "#2563eb" }}>
              Booking #{thread.bookingId}
            </Link>
          </p>
        )}
      </div>

      {/* Messages */}
      <div style={messageList}>
        {messages.length === 0 ? (
          <p style={{ fontSize: 14, color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>
            No messages yet. Say hello!
          </p>
        ) : (
          messages.map((m: any) => {
            const mine = m.senderId === currentUserId;
            return (
              <div key={m.id} style={bubbleWrap(mine)}>
                <div style={bubble(mine)}>{m.body}</div>
                <span style={timestamp}>{fmtTime(m.createdAt)}</span>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-end" }}>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
          rows={3}
          style={composeBox}
        />
        <button onClick={handleSend} disabled={sending || !body.trim()} style={sendBtn}>
          {sending ? "…" : "Send"}
        </button>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={() => refetch()} style={refreshBtn}>Refresh</button>
      </div>
    </div>
  );
}

const messageList: React.CSSProperties = {
  border: "1px solid #e5e7eb", borderRadius: 8, padding: 16,
  minHeight: 300, maxHeight: 500, overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 12,
  background: "#f9fafb",
};

const bubbleWrap = (mine: boolean): React.CSSProperties => ({
  display: "flex", flexDirection: "column",
  alignItems: mine ? "flex-end" : "flex-start",
});

const bubble = (mine: boolean): React.CSSProperties => ({
  maxWidth: "75%", padding: "8px 12px", borderRadius: 12,
  fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap",
  background: mine ? "#2563eb" : "#e5e7eb",
  color: mine ? "#fff" : "#111827",
});

const timestamp: React.CSSProperties = {
  fontSize: 11, color: "#9ca3af", marginTop: 2,
};

const composeBox: React.CSSProperties = {
  flex: 1, padding: "8px 12px", border: "1px solid #d1d5db",
  borderRadius: 8, fontSize: 14, resize: "none", fontFamily: "inherit",
};

const sendBtn: React.CSSProperties = {
  padding: "0 20px", height: 42, background: "#2563eb",
  color: "#fff", border: "none", borderRadius: 8,
  cursor: "pointer", fontSize: 14, fontWeight: 600,
  alignSelf: "flex-end",
};

const refreshBtn: React.CSSProperties = {
  fontSize: 12, color: "#6b7280", background: "none",
  border: "none", cursor: "pointer", textDecoration: "underline",
};
